import React, { useState, useEffect } from "react";
import { TbPlus } from "react-icons/tb";
import { useOrganizations } from "../../hooks/useOrganizations";
import { useAssessmentTemplates, useReviewLibrary } from "../../hooks/useAssessmentTemplates";
import { api } from "../../lib/api";
import { STATE_LABEL, TYPE_LABEL } from "../../lib/ratings";
import { Button, Field, Alert, Modal } from "../../components";
import OrganizationFields, {
  EMPTY_ORGANIZATION, validateOrganization, cleanOrganization,
} from "../organizations/OrganizationFields";
import "./Reviews.css";

// What this assessment is FOR — the conversation it is meant to support.
//
// Called "Assessment type" until now, next to a list that reads like content types, one
// step before the screen that actually picks the content. So it read as a template
// choice made twice, and the descriptions ("Capture the current environment") reinforced
// that by describing what the questions would cover.
//
// It is not a content choice: the questions and categories come entirely from the
// template or clone chosen on step two. What this sets is the framing — the title it
// suggests, how the assessment is labelled and filtered on the board, and the narrative
// the deliverable is written for. So each line below names the conversation, not the
// content.
const TYPES = [
  { value: "baseline", label: "Baseline", desc: "Where things stand today, before any changes" },
  { value: "proposed", label: "Proposed", desc: "The case for a solution you are recommending" },
  { value: "business_review", label: "Business review", desc: "Progress and priorities since the last review" },
];

const MIN_ITEMS = 9;

// Three steps: details, starting point, then the item pick.
//
// A template is copied at its published version, so a later edit to the template cannot
// rewrite an assessment a client has already been shown. Cloning copies a previous
// assessment WITH its ratings, which is the point of it: a quarterly review starts from
// last quarter's answers and the consultant changes what moved.
const NewAssessmentModal = ({ onClose, onCreate, assessments = [], canCreateOrganization = false }) => {
  const { organizations, loading: orgsLoading, create: createOrganization } = useOrganizations();
  const { templates } = useAssessmentTemplates();
  // The template's content carries item ids only, so the library supplies their names.
  const { items: library } = useReviewLibrary();

  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [source, setSource] = useState("template"); // template | clone
  const [templateItems, setTemplateItems] = useState(null);
  const [form, setForm] = useState({
    organization_id: "",
    title: "",
    type: "business_review",
    assessment_date: new Date().toISOString().slice(0, 10),
    template_id: "",
    clone_from: "",
    item_ids: null, // null = every item the source carries
  });

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  // A new organization, made here rather than on the Organizations screen — the client you
  // are about to review is often one you have not added yet, and leaving the dialog to add
  // it threw away everything typed so far. Inline under the picker, not a second dialog on
  // top of this one. Only offered to admins: the server refuses anyone else.
  const NEW_ORG = "__new__";
  const [orgDraft, setOrgDraft] = useState(null); // null = closed, else the organization fields
  const [orgBusy, setOrgBusy] = useState(false);
  const [orgError, setOrgError] = useState("");
  const noOrganizations = !orgsLoading && organizations.length === 0;

  // With nothing to pick from, the form is the only useful thing on offer: open it.
  useEffect(() => {
    if (noOrganizations && canCreateOrganization) setOrgDraft((d) => d || EMPTY_ORGANIZATION);
  }, [noOrganizations, canCreateOrganization]);

  const openOrgForm = () => { setOrgError(""); setOrgDraft(EMPTY_ORGANIZATION); };
  const saveOrganization = async () => {
    const invalid = validateOrganization(orgDraft);
    if (invalid) { setOrgError(invalid); return; }
    setOrgBusy(true); setOrgError("");
    try {
      const data = await createOrganization(cleanOrganization(orgDraft));
      const id = data?.organization?.id;
      if (id) set({ organization_id: String(id) });
      setOrgDraft(null);
    } catch (e) {
      setOrgError(e.message || "Could not create the organization.");
    } finally {
      setOrgBusy(false);
    }
  };
  // Both conditions: a template that was published and later moved back to draft still
  // has a current_version, but is no longer on offer. The server enforces this too.
  const published = templates.filter((t) => t.current_version > 0 && t.state === "published");

  // Only assessments for the chosen organization can be cloned: a clone carries that
  // client's ratings, and copying them onto a different client would be worse than
  // useless.
  const clonable = assessments.filter(
    (a) => String(a.organization_id) === String(form.organization_id)
  );

  // The chosen template's items, so step three can show what is about to be created.
  useEffect(() => {
    if (source !== "template" || !form.template_id) { setTemplateItems(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get(`s4/api/review-templates/${form.template_id}`);
        if (cancelled) return;
        const categories = (data?.template?.content?.categories || []).filter((c) => c.active !== false);
        setTemplateItems(categories);
        set({ item_ids: null });
      } catch {
        if (!cancelled) setTemplateItems(null);
      }
    })();
    return () => { cancelled = true; };
  }, [source, form.template_id]);

  const nameById = new Map(library.map((i) => [i.id, i.name]));
  const allTemplateIds = (templateItems || []).flatMap((c) => c.itemIds || []);
  const selected = form.item_ids === null ? allTemplateIds : form.item_ids;

  const canAdvance = step === 1
    ? !!form.organization_id && !!form.assessment_date && !orgDraft
    : source === "template" ? !!form.template_id : !!form.clone_from;

  const submit = async () => {
    setBusy(true); setError("");
    try {
      const payload = {
        organization_id: form.organization_id,
        title: form.title,
        type: form.type,
        assessment_date: form.assessment_date,
      };
      if (source === "clone") {
        payload.clone_from = Number(form.clone_from);
      } else {
        payload.template_id = Number(form.template_id);
        // Only send a subset when one was actually made. Sending the full list would be
        // the same thing, but null says "everything the template carries" and survives
        // the template gaining an item between here and the server.
        if (form.item_ids !== null) payload.item_ids = form.item_ids;
      }
      await onCreate(payload);
      onClose();
    } catch (e) {
      setError(e.message || "Could not create the assessment.");
    } finally { setBusy(false); }
  };

  const toggleItem = (id) =>
    set({ item_ids: selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id] });

  // Cloning has nothing to pick: it copies the source assessment whole.
  const lastStep = source === "clone" ? 2 : 3;

  return (
    <Modal
      title={`New assessment — step ${step} of ${lastStep}`}
      onClose={onClose}
      footer={<>
        {step > 1 && <Button onClick={() => setStep(step - 1)} disabled={busy}>Back</Button>}
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        {step < lastStep
          ? <Button variant="primary" disabled={!canAdvance} onClick={() => setStep(step + 1)}>Next</Button>
          : <Button variant="primary" disabled={!canAdvance || busy || (step === 3 && selected.length < MIN_ITEMS)}
                    onClick={submit}>
              {busy ? "Creating…" : "Create"}
            </Button>}
      </>}
    >
      <Alert>{error}</Alert>

      {step === 1 && (
        <>
          <Field label="Organization" hint="The scanner's own client list. Business reviews does not keep a second one.">
            <div className="v2-orgpick">
              <select className="v2-input" value={form.organization_id}
                      disabled={!!orgDraft && noOrganizations}
                      onChange={(e) => {
                        if (e.target.value === NEW_ORG) openOrgForm();
                        else set({ organization_id: e.target.value });
                      }}>
                <option value="">{noOrganizations ? "No organizations yet" : "Choose an organization…"}</option>
                {organizations.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                {canCreateOrganization && <option value={NEW_ORG}>+ New organization…</option>}
              </select>
              {canCreateOrganization && !orgDraft && (
                <Button onClick={openOrgForm}><TbPlus size={15} /> New</Button>
              )}
            </div>
          </Field>

          {orgDraft && (
            <div className="v2-orgcreate" role="group" aria-label="New organization">
              <div className="v2-orgcreate-head">
                <strong>New organization</strong>
                <span>It's added to your organizations and selected for this assessment.</span>
              </div>
              <Alert>{orgError}</Alert>
              {/* The same fields as New organization on the Organizations screen. */}
              <OrganizationFields value={orgDraft} onChange={setOrgDraft} onSubmit={saveOrganization} compact />
              <div className="v2-orgcreate-actions">
                {!noOrganizations && (
                  <Button onClick={() => setOrgDraft(null)} disabled={orgBusy}>Cancel</Button>
                )}
                <Button variant="primary" onClick={saveOrganization} disabled={orgBusy || !orgDraft.name.trim()}>
                  {orgBusy ? "Creating…" : "Create organization"}
                </Button>
              </div>
            </div>
          )}

          {noOrganizations && !canCreateOrganization && (
            <p className="v2-hint">Ask a workspace admin to add an organization before starting an assessment.</p>
          )}

          <Field label="Assessment purpose">
            {/* Above the options, not in Field's `hint`, which renders underneath them:
                this is how to read the three choices, so it is worth nothing once you
                have already made one. */}
            <p className="v2-hint" style={{ margin: "0 0 .5rem" }}>
              Choose the purpose now. You'll choose the questions and categories on the
              next step.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: ".375rem" }}>
              {TYPES.map((t) => (
                <button key={t.value} type="button"
                        className={`v2-radio-row${form.type === t.value ? " on" : ""}`}
                        aria-pressed={form.type === t.value}
                        onClick={() => set({ type: t.value })}>
                  <span className="v2-radio-dot" />
                  <span>
                    <span className="v2-radio-name">{t.label}</span>
                    <span className="v2-radio-desc">{t.desc}</span>
                  </span>
                </button>
              ))}
            </div>
          </Field>

          <Field label="Title" hint="Left blank, this takes the organization's name.">
            <input className="v2-input" value={form.title} onChange={(e) => set({ title: e.target.value })}
                   placeholder="Q1 business review" />
          </Field>

          <Field label="Assessment date">
            <input className="v2-input" type="date" value={form.assessment_date}
                   onChange={(e) => set({ assessment_date: e.target.value })} />
          </Field>
        </>
      )}

      {step === 2 && (
        <>
          <Field label="Starting point">
            <div className="v2-seg" role="tablist" aria-label="Starting point">
              <button type="button" role="tab" aria-selected={source === "template"}
                      className={source === "template" ? "on" : ""}
                      onClick={() => { setSource("template"); set({ clone_from: "" }); }}>
                Use a template
              </button>
              <button type="button" role="tab" aria-selected={source === "clone"}
                      className={source === "clone" ? "on" : ""}
                      onClick={() => { setSource("clone"); set({ template_id: "" }); }}>
                Clone a prior assessment
              </button>
            </div>
          </Field>

          {source === "template" ? (
            <>
              <div style={{ display: "flex", flexDirection: "column", gap: ".375rem" }}>
                {published.map((t) => (
                  <button key={t.id} type="button"
                          className={`v2-radio-row${String(form.template_id) === String(t.id) ? " on" : ""}`}
                          aria-pressed={String(form.template_id) === String(t.id)}
                          onClick={() => set({ template_id: t.id })}>
                    <span className="v2-radio-dot" />
                    <span>
                      <span className="v2-radio-name">{t.name}</span>
                      <span className="v2-radio-desc">
                        {t.category_count} categories · {t.item_count} items · Version {t.current_version}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
              <p className="v2-hint" style={{ marginTop: ".5rem" }}>
                A published version is copied in whole, so a later template edit cannot
                rewrite an assessment a client has already seen.
              </p>
              {published.length === 0 && (
                <Alert tone="warning">
                  No template has been published yet. Build one on the Assessment
                  templates screen and publish it — an assessment is always created from a
                  published version.
                </Alert>
              )}
            </>
          ) : (
            <>
              <div style={{ display: "flex", flexDirection: "column", gap: ".375rem" }}>
                {clonable.map((a) => (
                  <button key={a.id} type="button"
                          className={`v2-radio-row${String(form.clone_from) === String(a.id) ? " on" : ""}`}
                          aria-pressed={String(form.clone_from) === String(a.id)}
                          onClick={() => set({ clone_from: a.id })}>
                    <span className="v2-radio-dot" />
                    <span>
                      <span className="v2-radio-name">{a.title}</span>
                      <span className="v2-radio-desc">
                        {TYPE_LABEL[a.type] || a.type} · {STATE_LABEL[a.state] || a.state}
                        {" · "}{String(a.assessment_date).slice(0, 10)}
                        {a.score !== null && a.score !== undefined ? ` · scored ${a.score}` : ""}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
              <p className="v2-hint" style={{ marginTop: ".5rem" }}>
                A clone carries the previous ratings across, so the review starts from last
                time's answers and you change what has moved.
              </p>
              {clonable.length === 0 && (
                <Alert tone="warning">
                  This organization has no earlier assessment to clone. Start from a
                  template instead.
                </Alert>
              )}
            </>
          )}
        </>
      )}

      {step === 3 && (
        <>
          <Field label="Items">
            <div className="v2-hint">
              {selected.length} of {allTemplateIds.length} selected
              {selected.length < MIN_ITEMS && (
                <span className="v2-rbadge v2-rbadge--warning" style={{ marginLeft: ".5rem" }}>
                  {MIN_ITEMS - selected.length} MORE NEEDED
                </span>
              )}
            </div>
          </Field>

          {(templateItems || []).map((c) => (
            <div key={c.name} style={{ marginBottom: ".75rem" }}>
              <div className="v2-section-title">{c.name}</div>
              <div className="v2-picklist" style={{ maxHeight: "none", marginTop: ".375rem" }}>
                {(c.itemIds || []).map((id) => (
                  <button key={id} type="button"
                          className={`v2-pick${selected.includes(id) ? " on" : ""}`}
                          aria-pressed={selected.includes(id)}
                          onClick={() => toggleItem(id)}>
                    <span className={`v2-check${selected.includes(id) ? " on" : ""}`} aria-hidden="true" />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span className="v2-slot-name">{nameById.get(id) || `Item ${id}`}</span>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ))}

          <p className="v2-hint">
            Every item is included by default. Leave it alone unless this client genuinely
            does not need one — at least {MIN_ITEMS} must remain.
          </p>
        </>
      )}
    </Modal>
  );
};

export default NewAssessmentModal;
